import noDataImg from "../../../assets/img/no-data.png"
import { CompactTable } from "@table-library/react-table-library/compact"
import classNames from "classnames"
import { SelectPicker } from "rsuite"
import React, { useEffect, useState } from "react"
import useTranslate from "../../../hooks/useTranslate"
import { formatDate } from "../../../utils/datetime"
import {
  barChartHorizontalConfig,
  GroupingType,
  higherIcon, lineChartConfig, lineChartConfigWithThreshold,
  lowerIcon,
  thReachedColor, thTopColor
} from "./PatientGameStatsCommon"
import { usePagination } from "@table-library/react-table-library/pagination"
import { useTheme } from "@table-library/react-table-library/theme"
import { getTheme } from "@table-library/react-table-library/mantine"
import { NBackPlayedGameResultDto } from "../../../sdk"
import { Chart } from "chart.js/auto"
import dayjs from "dayjs"

type PlayedGameData = NBackPlayedGameResultDto & { id: number }

interface ArithmeticGameStatsProps {
  fullData: PlayedGameData[]
  filteredData: PlayedGameData[]
}

export function NBackGameStats({fullData, filteredData}: ArithmeticGameStatsProps) {
  const translateLocalazy = useTranslate()
  const theme = useTheme(getTheme())
  const [expandedIds, setExpandedIds] = useState<number[]>([])
  const [grouping, setGrouping] = useState<GroupingType | null>(null)

  useEffect(() => {
    const charts: Chart[] = []

    let playedGames = [...filteredData]
      .sort((a, b) => dayjs(a.playedAt).diff(b.playedAt, 'seconds'))

    if (grouping != null) {
      const grouped = playedGames.reduce((acc, game) => {
        const key = dayjs(game.playedAt).startOf(grouping).toISOString()
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push(game)
        return acc
      }, {} as Record<string, PlayedGameData[]>)

      playedGames = Object.entries(grouped).map(([date, e], i) => ({
        id: i,
        playedAt: date,
        points: Math.round(e.reduce((acc, game) => acc + game.points, 0) / e.length),
        successPercent: Math.round(e.reduce((acc, game) => acc + game.successPercent, 0) / e.length),
        enabledFeatures: [],
        maxPoints: Math.round(e.reduce((acc, game) => acc + game.maxPoints, 0) / e.length),
        mistakes: Math.round(e.reduce((acc, game) => acc + game.mistakes, 0) / e.length),
        rightAnswersPerFeature: [],
        stepsBack: Math.round(e.reduce((acc, game) => acc + game.stepsBack, 0) / e.length),
        gameLengthSeconds: 0
      }))
        .sort((a, b) => dayjs(a.playedAt).diff(b.playedAt, 'seconds'))
    }

    const successRateTh = 85
    const successRateData = {
      labels: playedGames.map(e => dayjs(e.playedAt).format('MM/DD')),
      datasets: [
        {
          label: translateLocalazy('patient.game-stats.chart.accuracy.title'),
          data: playedGames.map(e => e.successPercent),
          borderColor: '#8fbc6b',
          backgroundColor: 'rgba(82, 158, 0, 0.15)',
          fill: true,
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
          pointBackgroundColor: (ctx) =>
            ctx.parsed?.y < successRateTh ? thReachedColor : thTopColor,
          pointBorderColor: (ctx) =>
            ctx.parsed?.y < successRateTh ? thReachedColor : thTopColor,
        },
      ]
    }
    const pointsTh = 20
    const pointsData = {
      labels: playedGames.map(e => dayjs(e.playedAt).format('MM/DD')),
      datasets: [
        {
          label: translateLocalazy('patient.game-stats.chart.points.title'),
          data: playedGames.map(e => e.points),
          borderColor: '#8fbc6b',
          backgroundColor: 'rgba(82, 158, 0, 0.15)',
          fill: true,
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
          pointBackgroundColor: (ctx) =>
            ctx.parsed?.y < pointsTh ? thReachedColor : thTopColor,
          pointBorderColor: (ctx) =>
            ctx.parsed?.y < pointsTh ? thReachedColor : thTopColor,
        },
      ]
    }
    const stepsBackData = {
      labels: playedGames.map(e => dayjs(e.playedAt).format('MM/DD')),
      datasets: [
        {
          label: translateLocalazy('patient.game-stats.chart.steps-back.title'),
          data: playedGames.map(e => e.stepsBack),
          borderColor: '#8fbc6b',
          backgroundColor: 'rgba(82, 158, 0, 0.15)',
          fill: true,
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
      ]
    }
    const mistakesData = {
      labels: playedGames.map(e => dayjs(e.playedAt).format('MM/DD')),
      datasets: [
        {
          label: translateLocalazy('patient.game-stats.chart.mistakes.title'),
          data: playedGames.map(e => e.mistakes),
          borderColor: '#8fbc6b',
          backgroundColor: 'rgba(82, 158, 0, 0.15)',
          fill: true,
          pointStyle: 'circle',
          pointRadius: 4,
          pointHoverRadius: 8,
          cubicInterpolationMode: 'monotone',
        },
      ]
    }

    const successRateChartCtx = document.getElementById(`success-rate-chart-nback`)
    if (successRateChartCtx) {
      // @ts-ignore
      charts.push(new Chart(successRateChartCtx, {
        ...lineChartConfigWithThreshold(successRateTh, `${successRateTh}%`),
        data: successRateData
      }))
    }
    const pointsChartCtx = document.getElementById(`points-chart-nback`)
    if (pointsChartCtx) {
      // @ts-ignore
      charts.push(new Chart(pointsChartCtx, {
        ...lineChartConfigWithThreshold(pointsTh, `${pointsTh}`),
        data: pointsData
      }))
    }
    const stepsChartCtx = document.getElementById(`steps-back-nback`)
    if (stepsChartCtx) {
      // @ts-ignore
      charts.push(new Chart(stepsChartCtx, {
        ...lineChartConfig(6),
        data: stepsBackData
      }))
    }
    const mistakesChartCtx = document.getElementById(`mistakes-nback`)
    if (mistakesChartCtx) {
      // @ts-ignore
      charts.push(new Chart(mistakesChartCtx, {
        ...lineChartConfig(),
        data: mistakesData
      }))
    }

    return () => {
      charts.map(c => c.destroy())
    }
  }, [filteredData, grouping])

  useEffect(() => {
    const charts: Chart[] = []
    for (const id of expandedIds) {
      const game = fullData[id - 1]
      const gameData = (game.rightAnswersPerFeature ?? []).filter(e => game.enabledFeatures.includes(e.feature))
      const maxPointsPerFeature = game.maxPoints / game.enabledFeatures.length
      const chartData = {
        labels: gameData.map((e) => translateLocalazy('patient.game-stats.chart.feature.' + e.feature)),
        datasets: [
          {
            label: translateLocalazy('patient.game-stats.chart.points-per-feature'),
            data: gameData.map(e => e.answers),
            barPercentage: 0.4,
            borderColor: '#8fbc6b',
            hoverBorderColor: '#8fbc6b',
            backgroundColor: '#8fbc6b',
            hoverBackgroundColor: '#72ab42',
            borderWidth: 2,
            borderRadius: {
              topRight: 5,
              bottomRight: 5
            },
          }
        ]
      }

      const ctx = document.getElementById(`answer-per-feature-${id}-nback`)
      const config = barChartHorizontalConfig(translateLocalazy('patient.game-stats.chart.n-back.feature-answer.title'), maxPointsPerFeature)

      // @ts-ignore
      const chart = new Chart(ctx, {...config, data: chartData})
      charts.push(chart)
    }

    return () => {
      charts.map(c => c.destroy())
    }
  }, [expandedIds])

  const handleExpand = (item: PlayedGameData) => {
    if (expandedIds.includes(item.id)) {
      setExpandedIds(expandedIds.filter((id) => id !== item.id))
    } else {
      setExpandedIds(expandedIds.concat(item.id))
    }
  }

  const rowProps = {
    onClick: handleExpand,
  }
  const rowOptions = {
    renderAfterRow: (item: PlayedGameData) => (
      <>
        {expandedIds.includes(item.id) && <tr style={{display: "flex", gridColumn: "1 / -1", position: 'relative'}}>
            <canvas id={`answer-per-feature-${item.id}-nback`}></canvas>
        </tr>}
      </>
    ),
  }

  const tableColumns = [
    {label: '#', renderCell: (item) => fullData.length - item.id + 1},
    {
      label: translateLocalazy('patient.game-stats.chart.accuracy.title'), renderCell: (item) => {
        const prevItem = item.id === fullData.length ? null : fullData[item.id]

        if (prevItem) {
          if (prevItem.successPercent > item.successPercent) {
            return <span className="lower">
              {lowerIcon}
              {item.successPercent}%</span>
          } else if (prevItem.successPercent < item.successPercent) {
            return <span className="higher">
              {higherIcon}
              {item.successPercent}%</span>
          }
        }

        return <span className="same">{item.successPercent}%</span>
      }
    },
    {
      label: translateLocalazy('patient.game-stats.chart.points.title'), renderCell: (item) => {
        const prevItem = item.id === fullData.length ? null : fullData[item.id]

        if (prevItem) {
          if (prevItem.points > item.points) {
            return <span className="lower">
              {lowerIcon}
              {item.points}</span>
          } else if (prevItem.points < item.points) {
            return <span className="higher">
              {higherIcon}
              {item.points}</span>
          }
        }
        return <span className="same">{item.points}</span>
      }
    },
    {
      label: translateLocalazy('patient.game-stats.chart.steps-back.title'), renderCell: (item) => {
        return item.stepsBack
      }
    },
    {
      label: translateLocalazy('patient.game-stats.chart.mistakes.title'), renderCell: (item) => {
        return item.mistakes
      }
    },
    {
      label: translateLocalazy('patient.game-stats.played-at'),
      renderCell: (item) => formatDate(item.playedAt),
    },
  ]

  const pagination = usePagination({nodes: filteredData}, {
    state: {
      page: 0,
      size: 10,
    },
  })

  return <>
    {filteredData.length == 0 &&
        <div className="card-box no-data flex flex-column">
            <img src={noDataImg}/>
            <div className="no-data-text">{fullData.length === 0 ?
              translateLocalazy('patient.game-stats.no-data') :
              translateLocalazy('patient.game-stats.no-data-for-period')}</div>
        </div>
    }

    {filteredData.length > 0 && <div className="card-box">
        <div>
            <CompactTable
                columns={tableColumns}
                data={{nodes: filteredData}}
                theme={theme}
                rowProps={rowProps}
                rowOptions={rowOptions}
                pagination={pagination}
            />
        </div>
        <br/>
        <div className="pagination">
            <span>Total Pages: {pagination.state.getTotalPages(filteredData)}</span>

            <span>
          {translateLocalazy('patient.game-stats.page')}&nbsp;

              {pagination.state.getPages(filteredData).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={classNames({
                    'active': pagination.state.page === i
                  })}
                  onClick={() => pagination.fns.onSetPage(i)}
                >
                  {i + 1}
                </button>
              ))}
        </span>
        </div>
    </div>}
    <div className="flex justify-end margin-me--btm">
      <SelectPicker
        name="group-by"
        menuAutoWidth={true}
        onChange={setGrouping}
        data={[
          {label: translateLocalazy('patient.game-stats.group-by-day'), value: GroupingType.day},
          {label: translateLocalazy('patient.game-stats.group-by-week'), value: GroupingType.week},
          {
            label: translateLocalazy('patient.game-stats.group-by-month'),
            value: GroupingType.month
          }]}
        placeholder={translateLocalazy('patient.game-stats.group-by-placeholder')}
      />
    </div>
    <div className="card-box">
      <canvas id="success-rate-chart-nback"></canvas>
    </div>
    <div className="card-box">
      <canvas id="points-chart-nback"></canvas>
    </div>
    <div className="card-box">
      <canvas id="steps-back-nback"></canvas>
    </div>
    <div className="card-box">
      <canvas id="mistakes-nback"></canvas>
    </div>
  </>
}

